import type { inferRouterInputs, inferRouterOutputs } from '@trpc/server';
import type { NextPageContext } from 'next';
import { httpBatchStreamLink, loggerLink } from '@trpc/client';
import { createTRPCNext } from '@trpc/next';

import type { AppRouter } from '@savvy/server/routers/_app';
import clientEnv from '@savvy/env/client';
import { trpcTransformer as transformer } from '@savvy/utils/trpc-transformer';

/**
 * Extend `NextPageContext` with meta data that can be picked up by `responseMeta()` when server-side rendering
 */
export type SSRContext = {
  /**
   * Set HTTP Status code
   * @example
   * const utils = trpc.useUtils();
   * if (utils.ssrContext) {
   *   utils.ssrContext.status = 404;
   * }
   */
  status?: number;
} & NextPageContext;

function getAuthToken() {
  if (typeof window !== 'undefined') {
    return localStorage.getItem('accessToken');
  }
  return null;
}

/**
 * A set of strongly-typed React hooks from your `AppRouter` type signature with `createReactQueryHooks`.
 * @see https://trpc.io/docs/v11/react#3-create-trpc-hooks
 */
export const trpc = createTRPCNext<AppRouter, SSRContext>({
  config({ ctx }) {
    /**
     * If you want to use SSR, you need to use the server's full URL
     * @see https://trpc.io/docs/v11/ssr
     */
    return {
      /**
       * @see https://trpc.io/docs/v11/client/links
       */
      links: [
        // adds pretty logs to your console in development and logs errors in production
        loggerLink({
          enabled: (opts) =>
            clientEnv.NEXT_PUBLIC_VERCEL_ENV === 'development' ||
            (opts.direction === 'down' && opts.result instanceof Error),
        }),
        httpBatchStreamLink({
          url: typeof window !== 'undefined' ? '/api/trpc' : `${clientEnv.BASE_URL}/api/trpc`,
          /**
           * Send queries as POST with input in the request body instead of the
           * URL. Batched backtest queries carry the household's full positions
           * array; serialized into the URL they exceeded Vercel's edge limit
           * and 414'd before the function ran. Mutations are unchanged. The API
           * handler must keep `allowMethodOverride: true` (`pages/api/trpc/[trpc].ts`)
           * or POST'd queries 405.
           * @see https://trpc.io/docs/client/links/httpBatchStreamLink
           */
          methodOverride: 'POST',
          /**
           * Set custom request headers on every request from tRPC
           * @see https://trpc.io/docs/v11/ssr
           */
          headers: () => {
            const token = getAuthToken();
            const impersonatedUserId = localStorage.getItem('impersonatedUserId');
            const headers: Record<string, string> = {
              'Content-Type': 'application/json',
            };

            if (token) {
              headers['Authorization'] = `Bearer ${token}`;
            }

            if (impersonatedUserId) {
              headers['X-Impersonated-User'] = impersonatedUserId;
            }

            // If we're in SSR context, forward the headers
            if (ctx?.req?.headers) {
              const { connection: _connection, ...restHeaders } = ctx.req.headers;
              const combinedHeaders = {
                ...headers,
                ...restHeaders,
              };
              return combinedHeaders;
            }

            return headers;
          },
          /**
           * @see https://trpc.io/docs/v11/data-transformers
           */
          transformer,
        }),
      ],
      /**
       * @see https://tanstack.com/query/v5/docs/reference/QueryClient
       */
      // queryClientConfig: { defaultOptions: { queries: { staleTime: 60 } } },
    };
  },
  /**
   * @see https://trpc.io/docs/v11/ssr
   */
  ssr: false,
  /**
   * @see https://trpc.io/docs/v11/data-transformers
   */
  transformer,
});

export type RouterInput = inferRouterInputs<AppRouter>;
export type RouterOutput = inferRouterOutputs<AppRouter>;
